/* №2 «Машин хүлээн авах» drawer — улсын дугаараар тээврийн хэрэгслийн лавлагаа (Enter),
   олдохгүй бол гар бүртгэл, дараалалд нэмэх эсвэл шууд угаах */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { cwApi, type CwServiceDto, type CwVehicleInfoDto } from '@/api/carwash';
import { CAR_TYPES, CAR_TYPE_LABELS, carTypeInt, carTypeLabel } from '@/shared/data';
import { formatPlate, tug } from '@/shared/format';
import { IconCheck, IconHistory, IconLoader, IconPlay, IconPlus, IconSearch, IconUser, IconX } from '@/shared/icons';
import type { CarType, IntakeEntry } from '@/shared/types';

function IntakeSection({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-2">
      <div className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{label}</div>
      {children}
    </div>
  );
}

function priceFor(svc: CwServiceDto, carType: CarType): number {
  switch (carType) {
    case 'Жийп': return svc.PriceJeep;
    case 'Том оврын': return svc.PriceTruck;
    case 'Микроавтобус': return svc.PriceVan;
    default: return svc.PriceSedan;
  }
}

/* Лавлагааны мэдээллийн карт */
function VehicleCard({ v }: { v: CwVehicleInfoDto }) {
  const items: { label: string; value: string }[] = [
    { label: 'Марк / Загвар', value: [v.MarkName, v.ModelName].filter(Boolean).join(' ') || '—' },
    { label: 'Өнгө', value: v.ColorName || '—' },
    { label: 'Суудал', value: v.ManCount != null ? `${v.ManCount}` : '—' },
    { label: 'Овор (у×ө)', value: v.Length && v.Width ? `${v.Length}×${v.Width} мм` : '—' },
    { label: 'Хүрд', value: v.WheelPosition || '—' },
  ];
  return (
    <div className="rounded-[calc(var(--r)-2px)] bg-emerald-50 ring-1 ring-emerald-200 px-3 py-2.5 flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-[12px] font-bold text-emerald-800">
        <IconCheck size={14} strokeWidth={2.5} />
        Улсын бүртгэлээс олдлоо
      </div>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5">
        {items.map((it) => (
          <div key={it.label} className="leading-tight">
            <div className="text-[10px] font-semibold text-emerald-600/80">{it.label}</div>
            <div className="text-[13px] font-bold text-emerald-900">{it.value}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export interface IntakeDrawerProps {
  open: boolean;
  mobile: boolean;
  onClose: () => void;
  onSubmit: (entry: IntakeEntry) => void;
  busy?: boolean;
}

export function IntakeDrawer({ open, mobile, onClose, onSubmit, busy }: IntakeDrawerProps) {
  const [plate, setPlate] = useState('');
  const [carType, setCarType] = useState<CarType>('Суудлын');
  const [serviceIds, setServiceIds] = useState<string[]>([]);
  const [washerIds, setWasherIds] = useState<string[]>([]);
  const [vehicle, setVehicle] = useState<CwVehicleInfoDto | null>(null);
  const [manual, setManual] = useState(false);
  const plateRef = useRef<HTMLInputElement>(null);

  const servicesQ = useQuery({ queryKey: ['cw', 'services'], queryFn: cwApi.services, enabled: open });
  const washersQ = useQuery({ queryKey: ['cw', 'washers'], queryFn: cwApi.washers, enabled: open });
  const customersQ = useQuery({ queryKey: ['cw', 'customers'], queryFn: cwApi.customers, enabled: open });

  const lookupMut = useMutation({
    mutationFn: (p: string) => cwApi.vehicleByPlate(p),
    onSuccess: setVehicle,
    onError: () => setVehicle({ Found: false, PlateNumber: plate }),
  });

  const services = (servicesQ.data ?? []).filter((s) => s.IsActive);
  const washers = (washersQ.data ?? []).filter((w) => w.IsActive);

  useEffect(() => {
    if (open) {
      setPlate(''); setCarType('Суудлын'); setServiceIds([]); setWasherIds([]);
      setVehicle(null); setManual(false);
      /* default focus — УЛСЫН ДУГААР (slide transition дууссаны дараа) */
      const id = setTimeout(() => plateRef.current?.focus(), 320);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  const plateOk = /^\d{4} [А-ЯӨҮ]{2,3}$/.test(plate.trim());
  const customer = plateOk
    ? (customersQ.data ?? []).find((c) => c.Plate === plate.trim()) ?? null
    : null;

  useEffect(() => {
    if (customer) setCarType(carTypeLabel(customer.CarType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer?.Id]);

  const onPlateChange = (raw: string) => {
    setPlate(formatPlate(raw));
    setVehicle(null);
    setManual(false);
  };

  const lookup = () => {
    if (plateOk && !lookupMut.isPending) lookupMut.mutate(plate.trim());
  };

  const suggestions = plate.trim().length >= 2 && !customer
    ? (customersQ.data ?? []).filter((c) => c.Plate.startsWith(plate.trim())).map((c) => c.Plate).slice(0, 6)
    : [];

  const toggleService = (id: string) =>
    setServiceIds((sv) => (sv.includes(id) ? sv.filter((s) => s !== id) : [...sv, id]));

  const total = services
    .filter((s) => serviceIds.includes(s.Id))
    .reduce((sum, s) => sum + priceFor(s, carType), 0);

  /* Угаагч toggle — дээд тал нь 3 */
  const toggleWasher = (id: string) =>
    setWasherIds((ws) => {
      if (ws.includes(id)) return ws.filter((w) => w !== id);
      if (ws.length >= 3) return ws;
      return [...ws, id];
    });

  const vehicleOk = vehicle?.Found === true || manual;
  /* Лавлагаа хийгдээд олдоогүй + гар бүртгэл сонгоогүй үед л товч түгжигдэнэ */
  const vehicleBlocked = vehicle !== null && !vehicle.Found && !manual;
  const canSubmit = plateOk && serviceIds.length > 0 && washerIds.length > 0
    && !vehicleBlocked && !busy && !lookupMut.isPending;
  const missing = !plateOk ? 'Улсын дугаар оруулна уу'
    : serviceIds.length === 0 ? 'Үйлчилгээ сонгоно уу'
    : washerIds.length === 0 ? 'Угаагч сонгоно уу'
    : vehicleBlocked ? 'Мэдээлэл олдсонгүй — гар бүртгэлийг сонгоно уу' : null;

  /* Лавлагаа хийгээгүй байсан ч товч дарвал эхлээд автоматаар шалгаад дараа нь үйлдлээ хийнэ */
  const submit = async (startWashing: boolean) => {
    if (!canSubmit) return;
    if (!vehicleOk) {
      try {
        const v = await lookupMut.mutateAsync(plate.trim());
        // Олдоогүй бол зогсоно — анхааруулга + «Гар бүртгэлээр үргэлжлүүлэх» чек гарч ирнэ.
        if (!v.Found) return;
      } catch {
        return;
      }
    }
    onSubmit({ plate: plate.trim(), carType: carTypeInt(carType), serviceIds, washerIds, startWashing });
  };

  return (
    <div className={'absolute inset-0 z-40 ' + (open ? '' : 'pointer-events-none')}>
      {/* overlay */}
      <div onClick={onClose}
        style={{
          opacity: open ? 1 : 0,
          visibility: open ? 'visible' : 'hidden',
          transition: 'opacity .3s, visibility 0s linear ' + (open ? '0s' : '.3s'),
        }}
        className="absolute inset-0 bg-slate-900/40"></div>

      {/* panel */}
      <div
        style={{
          transform: mobile
            ? (open ? 'translateY(0)' : 'translateY(100%)')
            : (open ? 'translateX(0)' : 'translateX(100%)'),
          visibility: open ? 'visible' : 'hidden',
          transition: 'transform .3s ease-out, visibility 0s linear ' + (open ? '0s' : '.3s'),
        }}
        className={
        'absolute bg-white shadow-2xl flex flex-col ' +
        (mobile
          ? 'inset-x-0 bottom-0 top-7 rounded-t-[20px]'
          : 'top-0 right-0 h-full w-[480px] max-w-full')
      }>
        {/* толгой */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div className="font-bold text-slate-900 text-[16px]">Машин хүлээн авах</div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition">
            <IconX size={17} />
          </button>
        </div>

        {/* агуулга */}
        <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">
          {/* Улсын дугаар */}
          <IntakeSection label="Улсын дугаар">
            <div className="relative">
              <input
                ref={plateRef}
                value={plate}
                onChange={(e) => onPlateChange(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') lookup(); }}
                placeholder="1234 УБА"
                className="w-full h-14 pl-4 pr-12 rounded-[var(--r)] bg-slate-50 ring-1 ring-slate-200 text-[24px] font-extrabold tracking-[0.15em] text-slate-900 placeholder:text-slate-300 placeholder:font-bold focus:outline-none focus:ring-2 focus:ring-[var(--brand2)] focus:bg-white transition" />
              <button onClick={lookup} disabled={!plateOk || lookupMut.isPending} title="Мэдээлэл шалгах (Enter)"
                className={'absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 rounded-[10px] flex items-center justify-center transition ' +
                  (plateOk && !lookupMut.isPending
                    ? 'text-white bg-gradient-to-br from-[var(--brand1)] to-[var(--brand2)] hover:opacity-95'
                    : 'text-slate-300 bg-slate-100 cursor-not-allowed')}>
                {lookupMut.isPending ? <IconLoader size={17} className="animate-spin" /> : <IconSearch size={17} />}
              </button>
            </div>
            {suggestions.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map((p) => (
                  <button key={p} onClick={() => onPlateChange(p)}
                    className="text-[13px] font-bold text-slate-600 bg-slate-100 hover:bg-cyan-50 hover:text-cyan-800 rounded-full px-3 py-1.5 transition">
                    {p}
                  </button>
                ))}
              </div>
            )}

            {/* Тээврийн хэрэгслийн лавлагаа */}
            {lookupMut.isPending && (
              <div className="flex items-center gap-2 text-[13px] text-slate-500 rounded-[calc(var(--r)-2px)] bg-slate-50 ring-1 ring-slate-100 px-3 py-2.5">
                <IconLoader size={15} className="animate-spin text-[var(--brand2)]" />
                Улсын бүртгэлээс шалгаж байна…
              </div>
            )}
            {vehicle && vehicle.Found && <VehicleCard v={vehicle} />}
            {vehicle && !vehicle.Found && (
              <div className="rounded-[calc(var(--r)-2px)] bg-amber-50 ring-1 ring-amber-200 px-3 py-2.5 flex flex-col gap-2">
                <div className="text-[12px] font-bold text-amber-800">Улсын бүртгэлээс мэдээлэл олдсонгүй</div>
                <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-amber-900 cursor-pointer">
                  <input type="checkbox" checked={manual} onChange={(e) => setManual(e.target.checked)}
                    className="w-4 h-4 accent-amber-600" />
                  Гар бүртгэлээр үргэлжлүүлэх
                </label>
              </div>
            )}
            {!vehicle && !lookupMut.isPending && (
              <div className="text-[12px] text-slate-400">
                {customer
                  ? null
                  : 'Дугаараа бүтэн оруулаад Enter дарвал машины мэдээлэл шалгагдана'}
              </div>
            )}

            {customer && (
              <div className="flex items-center gap-2.5 rounded-[calc(var(--r)-2px)] bg-cyan-50 ring-1 ring-cyan-200 px-3 py-2.5">
                <span className="w-8 h-8 rounded-full bg-[var(--brand2)] text-white flex items-center justify-center">
                  <IconUser size={16} />
                </span>
                <div className="leading-tight min-w-0">
                  <div className="text-[13px] font-bold text-cyan-900">{customer.Name || 'Харилцагч'}{customer.Phone ? ` · ${customer.Phone}` : ''}</div>
                  <div className="text-[12px] text-cyan-700">{carTypeLabel(customer.CarType)}</div>
                </div>
                <span className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] rounded-full px-2.5 py-1 whitespace-nowrap">
                  <IconHistory size={12} />
                  {customer.Visits + 1} дахь ирэлт
                </span>
              </div>
            )}
          </IntakeSection>

          {/* Машины төрөл */}
          <IntakeSection label="Машины төрөл">
            <div className="grid grid-cols-4 gap-1.5">
              {CAR_TYPE_LABELS.map((tname) => {
                const TIcon = CAR_TYPES[tname];
                const sel = carType === tname;
                return (
                  <button key={tname} onClick={() => setCarType(tname)}
                    className={
                      'h-[64px] rounded-[calc(var(--r)-2px)] flex flex-col items-center justify-center gap-1 text-[11px] font-semibold ring-1 transition ' +
                      (sel ? 'bg-cyan-50 ring-2 ring-[var(--brand2)] text-[var(--brand1)]' : 'bg-white ring-slate-200 text-slate-500 hover:ring-slate-300')
                    }>
                    <TIcon size={20} strokeWidth={sel ? 2.3 : 1.8} />
                    {tname}
                  </button>
                );
              })}
            </div>
          </IntakeSection>

          {/* Үйлчилгээ */}
          <IntakeSection label="Үйлчилгээ">
            <div className="grid grid-cols-2 gap-1.5">
              {services.map((svc) => {
                const sel = serviceIds.includes(svc.Id);
                return (
                  <button key={svc.Id} onClick={() => toggleService(svc.Id)}
                    className={
                      'relative min-h-[64px] rounded-[calc(var(--r)-2px)] px-3 py-2.5 text-left ring-1 transition flex flex-col justify-center gap-0.5 ' +
                      (sel ? 'bg-cyan-50 ring-2 ring-[var(--brand2)]' : 'bg-white ring-slate-200 hover:ring-slate-300')
                    }>
                    <span className={'text-[13px] font-semibold leading-snug ' + (sel ? 'text-[var(--brand1)]' : 'text-slate-700')}>{svc.Name}</span>
                    <span className={'text-[13px] font-bold ' + (sel ? 'text-cyan-700' : 'text-slate-500')}>{tug(priceFor(svc, carType))}</span>
                    {sel && (
                      <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-[var(--brand2)] text-white flex items-center justify-center">
                        <IconCheck size={12} strokeWidth={3} />
                      </span>
                    )}
                  </button>
                );
              })}
              {services.length === 0 && (
                <div className="col-span-2 text-[13px] text-slate-400 py-4 text-center">
                  {servicesQ.isLoading ? 'Ачааллаж байна…' : 'Идэвхтэй үйлчилгээ алга — Тохиргоо хэсгээс нэмнэ үү'}
                </div>
              )}
            </div>
          </IntakeSection>

          {/* Угаагч — 1-3 хүн зэрэг сонгоно */}
          <IntakeSection label="Угаагч (дээд тал нь 3)">
            <div className="flex flex-wrap gap-1.5">
              {washers.map((w) => {
                const sel = washerIds.includes(w.Id);
                return (
                  <button key={w.Id} onClick={() => toggleWasher(w.Id)}
                    disabled={!sel && washerIds.length >= 3}
                    className={
                      'inline-flex items-center gap-1.5 min-h-[44px] pl-1.5 pr-3 rounded-full ring-1 text-[13px] font-semibold transition ' +
                      (sel
                        ? 'bg-cyan-50 ring-2 ring-[var(--brand2)] text-[var(--brand1)]'
                        : washerIds.length >= 3
                          ? 'bg-slate-50 ring-slate-100 text-slate-300 cursor-not-allowed'
                          : 'bg-white ring-slate-200 text-slate-600 hover:ring-slate-300')
                    }>
                    <span className={'w-8 h-8 rounded-full text-[13px] font-bold text-white flex items-center justify-center ' + (sel ? 'bg-[var(--brand2)]' : 'bg-slate-400')}>
                      {w.Name[0]}
                    </span>
                    {w.Name}
                    <span className={'text-[11px] font-bold ' + (sel ? 'text-cyan-600' : 'text-slate-400')}>{w.ActiveCarCount}</span>
                  </button>
                );
              })}
              {washers.length === 0 && (
                <div className="text-[13px] text-slate-400 py-2">
                  {washersQ.isLoading ? 'Ачааллаж байна…' : 'Идэвхтэй угаагч алга — Тохиргоо хэсгээс нэмнэ үү'}
                </div>
              )}
            </div>
            {washerIds.length >= 3 && (
              <div className="text-[11px] text-amber-600">Дээд тал нь 3 угаагч сонгоно</div>
            )}
          </IntakeSection>
        </div>

        {/* хөл — нийт дүн + нэмэх / шууд угаах */}
        <div className="border-t border-slate-100 px-5 py-4 flex items-center gap-3 bg-white">
          <div className="leading-tight">
            <div className="text-[11px] font-semibold text-slate-400">Нийт дүн{serviceIds.length > 0 ? ` · ${serviceIds.length} үйлчилгээ` : ''}</div>
            <div className="text-[22px] font-extrabold text-slate-900">{tug(total)}</div>
          </div>
          <div className="ml-auto flex flex-col items-end gap-1">
            <div className="flex gap-1.5">
              <button onClick={() => void submit(true)} disabled={!canSubmit} title="Очергүй — шууд угааж эхэлнэ"
                className={
                  'inline-flex items-center gap-1.5 min-h-[48px] px-4 rounded-[var(--r)] text-[14px] font-semibold ring-1 transition active:scale-[0.98] ' +
                  (canSubmit
                    ? 'text-[var(--brand1)] bg-cyan-50 ring-cyan-200 hover:bg-cyan-100'
                    : 'text-slate-400 bg-slate-100 ring-slate-200 cursor-not-allowed')
                }>
                <IconPlay size={16} strokeWidth={2.5} />
                Шууд угаах
              </button>
              <button onClick={() => void submit(false)} disabled={!canSubmit}
                className={
                  'inline-flex items-center gap-2 min-h-[48px] px-5 rounded-[var(--r)] text-[15px] font-semibold transition active:scale-[0.98] ' +
                  (canSubmit
                    ? 'text-white bg-gradient-to-r from-[var(--brand1)] to-[var(--brand2)] shadow-md shadow-cyan-600/25 hover:opacity-95'
                    : 'text-slate-400 bg-slate-100 cursor-not-allowed')
                }>
                <IconPlus size={18} strokeWidth={2.5} />
                {lookupMut.isPending ? 'Шалгаж байна…' : busy ? 'Нэмж байна…' : 'Дараалалд нэмэх'}
              </button>
            </div>
            {missing && <div className="text-[11px] text-slate-400">{missing}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
